import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const hoverMagnifierMediaQuery = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#setupHoverMagnifier(signal);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Adds desktop product-image magnification to the visible carousel image.
   * @param {AbortSignal} signal
   */
  #setupHoverMagnifier(signal) {
    if (this.presentation !== 'carousel' || !hoverMagnifierMediaQuery.matches) return;

    const slideshowContainer = this.querySelector(':scope > slideshow-component > slideshow-container');
    if (!(slideshowContainer instanceof HTMLElement)) return;

    slideshowContainer.addEventListener('pointermove', this.#handleMagnifierPointerMove, { signal });
    slideshowContainer.addEventListener('pointerleave', this.#resetHoverMagnifier, { signal });
    slideshowContainer.addEventListener('pointercancel', this.#resetHoverMagnifier, { signal });
    slideshowContainer.addEventListener('pointerdown', this.#resetHoverMagnifier, { signal });

    slideshowContainer.querySelectorAll('slideshow-arrows .slideshow-control').forEach((control) => {
      control.addEventListener('pointerenter', this.#resetHoverMagnifier, { signal });
      control.addEventListener('focus', this.#resetHoverMagnifier, { signal });
    });
  }

  /**
   * Updates the magnifier origin so the zoom follows the pointer.
   * @param {PointerEvent} event
   */
  #handleMagnifierPointerMove = (event) => {
    if (!hoverMagnifierMediaQuery.matches) {
      this.#resetHoverMagnifier();
      return;
    }

    if (event.target instanceof Element && event.target.closest('slideshow-arrows .slideshow-control')) {
      this.#resetHoverMagnifier();
      return;
    }

    const slide = this.#activeMagnifierSlide;
    const image = slide?.querySelector('.product-media__image');

    if (!(image instanceof HTMLImageElement)) {
      this.#resetHoverMagnifier();
      return;
    }

    const origin = getContainedImageOrigin(image, event.clientX, event.clientY);

    this.style.setProperty('--product-gallery-hover-zoom-x', `${origin.x}%`);
    this.style.setProperty('--product-gallery-hover-zoom-y', `${origin.y}%`);
    this.setAttribute('data-product-gallery-magnifier-active', 'true');
  };

  #resetHoverMagnifier = () => {
    this.removeAttribute('data-product-gallery-magnifier-active');
    this.style.removeProperty('--product-gallery-hover-zoom-x');
    this.style.removeProperty('--product-gallery-hover-zoom-y');
  };

  get #activeMagnifierSlide() {
    return this.querySelector(
      ':scope > slideshow-component > slideshow-container > slideshow-slides > slideshow-slide[aria-hidden="false"].product-media-container--zoomable.product-media-container--image'
    );
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/**
 * Gets pointer origin as a percentage of the image element, clamped to the
 * actual object-fit: contain image area so letterboxed space does not pull focus.
 * @param {HTMLImageElement} image
 * @param {number} clientX
 * @param {number} clientY
 * @returns {{ x: number, y: number }}
 */
function getContainedImageOrigin(image, clientX, clientY) {
  const rect = image.getBoundingClientRect();

  if (!rect.width || !rect.height || !image.naturalWidth || !image.naturalHeight) {
    return { x: 50, y: 50 };
  }

  const imageRatio = image.naturalWidth / image.naturalHeight;
  const rectRatio = rect.width / rect.height;
  const contained = {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };

  if (rectRatio > imageRatio) {
    contained.width = rect.height * imageRatio;
    contained.left = rect.left + (rect.width - contained.width) / 2;
  } else {
    contained.height = rect.width / imageRatio;
    contained.top = rect.top + (rect.height - contained.height) / 2;
  }

  const clampedX = clamp(clientX, contained.left, contained.left + contained.width);
  const clampedY = clamp(clientY, contained.top, contained.top + contained.height);

  return {
    x: ((clampedX - rect.left) / rect.width) * 100,
    y: ((clampedY - rect.top) / rect.height) * 100,
  };
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
